import { Box } from "@outschool/backpack";
import {
  DefaultVariants,
  ExperimentVariants,
} from "@outschool/experiments-shared";
import { useSession } from "@outschool/ui-auth";
import { useOnScreen } from "@outschool/ui-utils";
import React, { useEffect, useRef } from "react";

import useExperiment from "../../hooks/useExperiment";
import { NoInfer } from "./NoInfer";

interface ExperimentTriggerWrapperProps<
  Variants extends ExperimentVariants = DefaultVariants,
> {
  /* The name of the experiment to trigger, should match the name in experiments admin. */
  name: string;
  /* Default variant to use if the experiment is not assigned. */
  defaultVariant?: NoInfer<Variants>;
  /* Child nodes to wrap. */
  children: React.ReactNode;
  /* Whether to disable triggering the experiment when the component is in the viewport. */
  disable?: boolean;
}

/**
 * Wraps a component used in the given experiment and triggers that experiment when that component enters the viewport.
 */
export default function ExperimentTriggerWrapper<
  Variants extends ExperimentVariants = DefaultVariants,
>({
  name,
  defaultVariant,
  disable = false,
  children,
}: ExperimentTriggerWrapperProps<Variants>) {
  const { trigger } = useExperiment<Variants>(name, defaultVariant);
  const { currentUserHasLoaded } = useSession();

  const nodeRef = useRef<HTMLDivElement>(null);

  const isOnScreen = useOnScreen(nodeRef.current);

  useEffect(() => {
    const blockTrigger = !currentUserHasLoaded || disable;

    if (isOnScreen && !blockTrigger) {
      trigger();
    }
  }, [isOnScreen, trigger, disable, name, currentUserHasLoaded, children]);

  return (
    // Need to wrap in a Box in order to correctly observe the component for useOnScreen.
    // Note: we may be able to remove this and use a cloning approach once we upgrade to React 19
    // See https://gitlab.com/outschool-eng/outschool/-/merge_requests/33366#note_2981683919
    <Box
      ref={nodeRef}
      sx={{
        ...(children === null
          ? { height: "1px", width: "1px", position: "absolute" }
          : {}),
      }}
    >
      {children}
    </Box>
  );
}
